// Single-station gate composed by AdminShell. Owns:
//   - usePublisherConfig loading + redirect when slug doesn't exist
//   - the shared auth + grant checks (AdminAuthGate)
//   - canAdminStation gate → /admin (picker) for users without scope
//
// CTV-759: the auth + grant half moved to AdminAuthGate so the aggregated
// shell can reuse it. The config fetch and canAdminStation stay here —
// /admin/all has no slug to resolve.

import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePublisherConfig } from '../../../data/publisherConfigs';
import { useAuth, type SelfServiceUser } from '../auth/useAuth';
import AdminAuthGate from './AdminAuthGate';
import type { PublisherConfig } from '../../../types/selfService';

interface Props {
  slug: string | undefined;
  children: (ctx: { config: PublisherConfig; user: SelfServiceUser; slug: string }) => React.ReactNode;
}

const PublisherAdminGate: React.FC<Props> = ({ slug, children }) => {
  const { config, loading } = usePublisherConfig(slug);
  const { canAdminStation } = useAuth();

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">Loading…</div>;
  }
  if (!slug || !config) return <Navigate to="/" replace />;

  return (
    <AdminAuthGate>
      {(user) => {
        if (!canAdminStation({
          name: config.name,
          organizationId: config.organizationId,
        })) {
          // CTV-415 — user is authed but not authorized for this slug. Send
          // them to the picker so they can pick a station they actually have
          // access to. Friendlier than a dead-end "No access" card.
          return <Navigate to="/admin" replace />;
        }
        return <>{children({ config, user, slug })}</>;
      }}
    </AdminAuthGate>
  );
};

export default PublisherAdminGate;
